// Comparing two versions of a plan.
//
// Line-level, because that is the unit somebody edits and the unit a reviewer
// asks about: "what changed in the scope section". A word-level diff reads
// better on a sentence and worse on a document, and this is a document.
//
// WHAT IT DOES NOT DO: guess. If one of the two versions could not be read —
// out of scope, or a request that failed — the caller says so and this is never
// invoked with half the input. A comparison that silently treats a missing
// version as empty would report the whole document as deleted, which is a
// wrong answer delivered confidently.

// Longest common subsequence over lines. The document is small enough that the
// quadratic table is fine: a plan is prose, not a log file.
function commonTable(a, b) {
    const table = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));

    for (let i = a.length - 1; i >= 0; i -= 1) {
        for (let j = b.length - 1; j >= 0; j -= 1) {
            table[i][j] = a[i] === b[j]
                ? table[i + 1][j + 1] + 1
                : Math.max(table[i + 1][j], table[i][j + 1]);
        }
    }

    return table;
}

// Returns [{ type: 'same' | 'added' | 'removed', text }].
export function diffLines(before = '', after = '') {
    const a = String(before ?? '').replace(/\r\n/g, '\n').split('\n');
    const b = String(after ?? '').replace(/\r\n/g, '\n').split('\n');

    const table = commonTable(a, b);
    const rows = [];
    let i = 0;
    let j = 0;

    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) {
            rows.push({ type: 'same', text: a[i] });
            i += 1; j += 1;
        } else if (table[i + 1][j] >= table[i][j + 1]) {
            rows.push({ type: 'removed', text: a[i] });
            i += 1;
        } else {
            rows.push({ type: 'added', text: b[j] });
            j += 1;
        }
    }

    while (i < a.length) { rows.push({ type: 'removed', text: a[i] }); i += 1; }
    while (j < b.length) { rows.push({ type: 'added', text: b[j] }); j += 1; }

    return rows;
}

export function countChanges(rows) {
    return {
        added: rows.filter(row => row.type === 'added').length,
        removed: rows.filter(row => row.type === 'removed').length,
    };
}

// Which repositories entered and which left.
//
// `[]` MEANS THE WHOLE PROJECT on both sides, so the comparison is about the
// scope and not about the list: going from the whole project to two
// repositories is a narrowing, and reporting it as "two repositories added"
// would say the opposite of what happened.
export function diffScope(before, after) {
    const wasAll = !Array.isArray(before) || before.length === 0;
    const isAll  = !Array.isArray(after)  || after.length === 0;

    if (wasAll && isAll) return { kind: 'same-all' };
    if (wasAll && !isAll) return { kind: 'narrowed', to: after };
    if (!wasAll && isAll) return { kind: 'widened', from: before };

    const beforeSet = new Set(before);
    const afterSet = new Set(after);

    return {
        kind: 'changed',
        added: after.filter(id => !beforeSet.has(id)),
        removed: before.filter(id => !afterSet.has(id)),
    };
}
